import type { Asset } from '@compass/client/types/asset';
import { AssetType } from '@compass/client/types/asset';
import { assetsService } from '@services/assets.service';
import { useStore } from '@store/store';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface CaseRecordContext {
  organizationId: string;
  programId: string;
  caseRecordId: string;
}

/**
 * Hook to fetch assets for a case record (not tied to a specific call)
 *
 * Use this hook when you need to fetch assets that are associated with the
 * case record directly (e.g., documents uploaded at case level), rather than
 * assets tied to a specific call (recordings, transcripts).
 *
 * @param caseRecordId - The ID of the case record to fetch assets for
 * @param assetType - Optional specific asset type to filter by
 * @param options - Optional configuration for polling behavior
 * @returns Query result containing array of assets
 */
export const useCaseRecordAssets = (
  caseRecordId: string | null,
  assetType?: AssetType,
  options?: {
    pollingInterval?: number;
    stopPollingWhenFound?: boolean;
    /**
     * Predicate deciding when polling has "found" what it was waiting for and can
     * stop (only consulted when `stopPollingWhenFound` is true). Defaults to
     * "any asset present". Pass a stricter predicate (e.g. "a COMPLETED asset
     * with a content_type") when a bare asset row appears before its content is
     * ready — otherwise polling stops too early and the consumer is left with an
     * unusable, half-created asset until a manual refetch.
     */
    isFound?: (assets: Asset[]) => boolean;
  },
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const pollingInterval = options?.pollingInterval ?? 10000; // Default 10 seconds
  const stopPollingWhenFound = options?.stopPollingWhenFound ?? true;
  const isFound = options?.isFound ?? ((assets: Asset[]) => assets.length > 0);

  const context: CaseRecordContext | null = useMemo(() => {
    if (!caseRecordId || !selectedOrganizationId || !selectedProgramId) return null;
    return {
      organizationId: selectedOrganizationId,
      programId: selectedProgramId,
      caseRecordId,
    };
  }, [caseRecordId, selectedOrganizationId, selectedProgramId]);

  return useQuery<Asset[], Error>({
    queryKey: ['caseRecordAssets', caseRecordId, assetType ?? 'all', testModeEnabled],
    queryFn: async () => {
      // Return empty response if in test mode
      if (testModeEnabled) {
        return [];
      }

      // Fetch assets by case record ID using the new scoped API
      // Pass undefined for callId to get all assets for the case record
      return assetsService.listAssets(context!, undefined, assetType);
    },
    enabled: !!context && !testModeEnabled,
    refetchInterval: stopPollingWhenFound
      ? query => {
          // Keep polling until the `isFound` predicate is satisfied. This is
          // deliberately not "any asset row exists": a call_document asset can
          // surface in the list while still PENDING/UPLOADING (no content_type,
          // nothing downloadable), and stopping then leaves the preview stuck on
          // an unusable asset until a manual refetch.
          const assets = query.state.data;
          return assets && isFound(assets) ? false : pollingInterval;
        }
      : pollingInterval, // Fixed polling interval if stopPollingWhenFound is false
  });
};
